import sharedSupabase from '../config/supabase';
import {
  detectParTransition,
  deterministicParRecommendation,
  fallbackParRecommendation,
  parTransitionKey,
} from '../utils/parTransition';

/**
 * The PAR package-transition ledger (par_package_transitions, migration
 * 20260807130000) and its recommendation flow.
 *
 * Sequence, per the AI rules this feature ships under:
 *   1. Deterministic detection (detectParTransition — pure, no I/O) decides
 *      whether a prompt is due at all. Most items never get one.
 *   2. A previously RESOLVED transition never prompts again; a previously
 *      cached suggestion is reused, so one transition never pays for two AI
 *      calls.
 *   3. Deterministic recommendation first; only the ambiguous middle asks the
 *      items-ai-match `par_recommendation` action, which re-verifies the
 *      conversion facts server-side and degrades to keep_quantity on any
 *      failure. The AI never sees a count keystroke — this runs at most once
 *      per (item, old package, new package).
 *   4. The user decides. Only applyParDecision writes a PAR, and only with
 *      the user's explicit choice.
 *
 * Everything degrades: a database without the table returns empty maps and
 * the whole feature quietly disappears, exactly like the other unit-model
 * services.
 */

const isMissingSchemaError = (error) => {
  const code = String(error?.code || '');
  const message = String(error?.message || '');
  return ['PGRST204', 'PGRST205', '42703', '42P01'].includes(code)
    || /relation .* does not exist|column .* does not exist|schema cache/i.test(message);
};

const keyOfRow = (row) =>
  parTransitionKey(row.inventory_item_id, row.old_purchase_option_id, row.new_purchase_option_id);

/**
 * Every stored transition row for a set of items, keyed by
 * `${itemId}::${oldOptionId}::${newOptionId}`. {} when the table is missing.
 */
export async function fetchParTransitions(storeId, inventoryItemIds, client = sharedSupabase) {
  const ids = [...new Set((inventoryItemIds || []).filter((id) => id != null))];
  if (!storeId || ids.length === 0) return {};
  try {
    const { data, error } = await client
      .from('par_package_transitions')
      .select('*')
      .eq('store_id', storeId)
      .in('inventory_item_id', ids);
    if (error) {
      if (!isMissingSchemaError(error)) {
        console.warn('[par-transition] fetch failed:', error.message);
      }
      return {};
    }
    const byKey = {};
    (data || []).forEach((row) => { byKey[keyOfRow(row)] = row; });
    return byKey;
  } catch (err) {
    console.warn('[par-transition] fetch threw:', err);
    return {};
  }
}

/**
 * Upserts the transition row for one detected transition (check-then-write,
 * because the identity is an expression index PostgREST cannot name in
 * ON CONFLICT). Returns the stored row, or null when the table is missing.
 */
export async function recordParTransition({
  storeId,
  inventoryItemId,
  transition,
  recommendation = null,
  decision = null,
  resolvedBy = null,
  resultingNormalizedPar = null,
  oldParDisplay = null,
}, client = sharedSupabase) {
  if (!storeId || inventoryItemId == null || !transition) return null;
  const identity = {
    store_id: storeId,
    inventory_item_id: inventoryItemId,
    old_purchase_option_id: transition.oldOptionId ?? null,
    new_purchase_option_id: transition.newOptionId ?? null,
  };
  const body = {
    ...identity,
    old_package_label: transition.oldPackageLabel ?? null,
    new_package_label: transition.newPackageLabel ?? null,
    old_qty_per_package: transition.oldQtyPerPackage ?? null,
    new_qty_per_package: transition.newQtyPerPackage ?? null,
    count_unit: transition.countUnit ?? null,
    old_normalized_par: transition.normalizedPar ?? null,
    old_par_display: oldParDisplay ?? (
      transition.parDisplayValue != null
        ? { value: transition.parDisplayValue, unit: transition.parDisplayUnit ?? null }
        : null
    ),
    ...(recommendation ? {
      recommendation,
      recommendation_source: recommendation.source ?? null,
    } : {}),
    ...(decision ? {
      decision,
      resolved_by: resolvedBy ?? null,
      resolved_at: new Date().toISOString(),
      resulting_normalized_par: resultingNormalizedPar,
    } : {}),
  };

  try {
    let query = client
      .from('par_package_transitions')
      .select('id')
      .eq('store_id', identity.store_id)
      .eq('inventory_item_id', identity.inventory_item_id);
    query = identity.old_purchase_option_id === null
      ? query.is('old_purchase_option_id', null)
      : query.eq('old_purchase_option_id', identity.old_purchase_option_id);
    query = identity.new_purchase_option_id === null
      ? query.is('new_purchase_option_id', null)
      : query.eq('new_purchase_option_id', identity.new_purchase_option_id);
    const { data: found, error: findError } = await query.maybeSingle();
    if (findError) {
      if (!isMissingSchemaError(findError)) {
        console.warn('[par-transition] lookup failed:', findError.message);
      }
      return null;
    }

    if (found?.id) {
      const { data, error } = await client
        .from('par_package_transitions')
        .update(body)
        .eq('id', found.id)
        .select()
        .single();
      if (error) throw new Error(error.message);
      return data;
    }
    const { data, error } = await client
      .from('par_package_transitions')
      .insert(body)
      .select()
      .single();
    if (error) throw new Error(error.message);
    return data;
  } catch (err) {
    console.warn('[par-transition] record failed:', err?.message || err);
    return null;
  }
}

/**
 * The recommendation for one transition: the stored cache, else deterministic,
 * else ONE server call (items-ai-match `par_recommendation`, which re-verifies
 * the conversions server-side and itself runs deterministic-first), else the
 * safe fallback. The result is cached on the transition row so the next count
 * never asks again.
 */
export async function getParRecommendation({
  storeId,
  inventoryItemId,
  transition,
  cachedRow = null,
}, client = sharedSupabase) {
  if (cachedRow?.recommendation?.recommendation) return cachedRow.recommendation;

  const deterministic = deterministicParRecommendation(transition);
  if (deterministic) {
    recordParTransition(
      { storeId, inventoryItemId, transition, recommendation: deterministic },
      client,
    );
    return deterministic;
  }

  let recommendation = null;
  try {
    if (client?.functions?.invoke) {
      const { data, error } = await client.functions.invoke('items-ai-match', {
        body: {
          action: 'par_recommendation',
          store_id: storeId,
          inventory_item_id: inventoryItemId,
          old_purchase_option_id: transition.oldOptionId,
          new_purchase_option_id: transition.newOptionId,
          normalized_par: transition.normalizedPar,
          par_display_value: transition.parDisplayValue,
          par_display_unit: transition.parDisplayUnit,
        },
      });
      if (error) throw error;
      recommendation = data?.recommendation ?? null;
    }
  } catch (err) {
    console.warn('[par-transition] AI recommendation skipped:', err?.message || err);
  }
  if (!recommendation?.recommendation) {
    recommendation = fallbackParRecommendation(transition);
  }
  recordParTransition({ storeId, inventoryItemId, transition, recommendation }, client);
  return recommendation;
}

/**
 * Detects pending prompts for a set of count rows: transitions that are due
 * (detectParTransition) and not already resolved. Pure over the inputs the
 * count screen already holds, plus one fetch of the stored ledger.
 *
 * Group-aware (Part 5 of the ordering correction): for a folded Equivalent
 * group the card's PAR is the GROUP's — the max normalized PAR across member
 * rows, whose provenance may live on a hidden sibling. `groupRowsFor` hands
 * this detector those member rows so a favored-member switch across siblings
 * is evaluated as old group presentation → new group presentation while the
 * normalized group PAR stays put. The transition stays keyed to the visible
 * canonical item's id (the group's stable ledger identity).
 *
 * @param {object} args
 * @param {number} args.storeId
 * @param {Array}  args.items        count rows (one per item is enough)
 * @param {Function} args.presentationFor  (item) => resolveCountPresentation result
 * @param {Function} args.parResolutionFor (item) => resolveRowPar result
 * @param {Function} args.optionsFor  (item) => purchase_options rows
 * @param {Function} [args.groupRowsFor] (item) => every member row whose PAR
 *   constitutes the group's PAR (visible + hidden); defaults to [item]
 * @returns {Promise<Object>} { [inventoryItemId]: { transition, storedRow|null } }
 */
export async function detectPendingParTransitions({
  storeId,
  items,
  presentationFor,
  parResolutionFor,
  optionsFor,
  groupRowsFor,
}, client = sharedSupabase) {
  const detected = {};
  const byItem = new Map();
  (items || []).forEach((item) => {
    const invId = item?.inventory_item_id;
    if (invId == null || byItem.has(String(invId))) return;
    byItem.set(String(invId), item);
  });

  byItem.forEach((item, invIdKey) => {
    const presentation = presentationFor(item);
    if (!presentation?.optionId) return;
    // The group's PAR: the max VALID normalized resolution across member
    // rows (just the item's own rows when no group is in play). The winner's
    // display/provenance travels with it, so a PAR entered as "3 Vendor A
    // cases" on a now-hidden sibling still asks its transition question when
    // Vendor B fronts the card.
    const memberRows = (typeof groupRowsFor === 'function' ? groupRowsFor(item) : null) || [item];
    let parResolution = null;
    memberRows.forEach((row) => {
      const resolution = row ? parResolutionFor(row) : null;
      if (resolution?.normalized != null
        && (parResolution === null || resolution.normalized > parResolution.normalized)) {
        parResolution = resolution;
      }
    });
    if (!parResolution || parResolution.normalized == null) return;

    const options = optionsFor(item) || [];
    const option = options.find((o) => String(o.id) === String(presentation.optionId)) || null;
    const packageRung = (presentation.rungs || []).find(
      (r) => r.level === 'package' && String(r.optionId ?? '') === String(presentation.optionId),
    ) || null;
    const snapshotQty = parResolution.snapshot?.quantity_per_package ?? null;

    const result = detectParTransition({
      normalizedPar: parResolution.normalized,
      parDisplay: parResolution.display,
      parQtyPerPackage: snapshotQty,
      newOptionId: presentation.optionId,
      newQtyPerPackage: packageRung?.qtyInCountUnit ?? null,
      newPackageLabel: packageRung?.label
        || [option?.vendor_name_text, option?.purchase_unit].filter(Boolean).join(' ')
        || option?.purchase_unit
        || null,
      oldPackageLabel: parResolution.display?.unit ?? null,
      countUnit: presentation.rung?.snapshot?.count_unit ?? item?.item_snapshot?.unit ?? null,
    });
    if ((result.required || result.autoResolve) && result.transition) {
      detected[invIdKey] = { transition: result.transition, item, autoResolve: !!result.autoResolve };
    }
  });

  if (Object.keys(detected).length === 0) return {};

  const stored = await fetchParTransitions(
    storeId,
    Object.values(detected).map(({ item }) => item.inventory_item_id),
    client,
  );

  const pending = {};
  Object.entries(detected).forEach(([invIdKey, { transition, item, autoResolve }]) => {
    const row = stored[
      parTransitionKey(item.inventory_item_id, transition.oldOptionId, transition.newOptionId)
    ] || null;
    // A decided transition never prompts again. 'dismissed' may quietly
    // return next count, which is exactly a dismissal's meaning. Auto-resolve
    // entries record keep_quantity when applied, so they also pass here once.
    if (row?.decision && row.decision !== 'dismissed') return;
    pending[invIdKey] = { transition, storedRow: row, item, autoResolve };
  });
  return pending;
}

/**
 * The exact-equivalent path (skip rule A): the normalized PAR lands on a whole
 * number of NEW packages, so there is no stocking question and no prompt. The
 * normalized PAR is untouched by construction — this only refreshes the
 * friendly display trio (par_display_quantity/unit + par_purchase_option_id)
 * so the PAR reads in the package the restaurant now actually uses, and
 * records the deterministic keep_quantity resolution in the ledger so the
 * transition never surfaces again. Best-effort everywhere: a database without
 * the display columns just keeps the old wording.
 */
export async function applyExactEquivalentRefresh({
  storeId,
  sessionId = null,
  inventoryItemId,
  transition,
}, client = sharedSupabase) {
  if (!transition?.newEquivalentIsWhole) return null;
  const wholePackages = Math.round(transition.newEquivalentPackages);
  const displayPatch = {
    par_purchase_option_id: transition.newOptionId ?? null,
    par_display_quantity: wholePackages,
    par_display_unit: transition.newPackageLabel ?? null,
  };

  if (sessionId != null && inventoryItemId != null) {
    try {
      const { error } = await client
        .from('count_items')
        .update(displayPatch)
        .eq('count_session_id', sessionId)
        .eq('inventory_item_id', inventoryItemId);
      if (error && !isMissingSchemaError(error)) {
        console.warn('[par-transition] exact-equivalent display refresh failed:', error.message);
      }
    } catch (err) {
      console.warn('[par-transition] exact-equivalent display refresh threw:', err?.message || err);
    }
  }

  const storedRow = await recordParTransition({
    storeId,
    inventoryItemId,
    transition,
    recommendation: deterministicParRecommendation(transition),
    decision: 'keep_quantity',
    resolvedBy: 'system:exact_equivalent',
    resultingNormalizedPar: transition.normalizedPar,
  }, client);

  return { displayPatch, storedRow };
}

/**
 * Applies the user's decision.
 *
 * keep_quantity: the normalized PAR does not move; only the ledger records
 * the answer (display/provenance stay as entered — "originally set as 1
 * Sysco case" remains true and readable).
 *
 * preserve_package_count: the PAR intentionally becomes `preservePackages` of
 * the NEW package. Both halves are written the way the template editor's
 * ParUnitControl writes them — par_level/par_unit in the count unit for the
 * legacy math, the display pair + normalized quintet for the unit model —
 * on every count row of the item in this session, and propagated to the
 * template rows so the next count inherits it.
 *
 * For a folded Equivalent group, `memberItemIds` names every SAME-UNIT member
 * whose rows carry the group's PAR (the fold's partition). The decision
 * writes to all of them — the same fan-out rule a count-page PAR edit uses —
 * because the group PAR is the max across members: leaving a sibling's old
 * higher PAR standing would silently override the user's explicit answer.
 */
export async function applyParDecision({
  storeId,
  sessionId,
  templateId = null,
  inventoryItemId,
  memberItemIds = null,
  transition,
  decision,
  recommendation = null,
  resolvedBy = null,
  countUnit,
}, client = sharedSupabase) {
  const resulting = decision === 'preserve_package_count'
    ? transition.preserveNormalizedPar
    : transition.normalizedPar;
  const targetItemIds = [...new Set(
    [inventoryItemId, ...(Array.isArray(memberItemIds) ? memberItemIds : [])]
      .filter((id) => id != null),
  )];

  if (decision === 'preserve_package_count') {
    const legacyParPatch = {
      par_level: transition.preserveNormalizedPar,
      par_unit: countUnit ?? null,
    };
    const parPatch = { ...legacyParPatch, par_defaulted: false };
    const normalizationPatch = {
      par_purchase_option_id: transition.newOptionId ?? null,
      par_normalized_quantity: transition.preserveNormalizedPar,
      par_normalized_unit: countUnit ?? null,
      par_display_quantity: transition.preservePackages,
      par_display_unit: transition.newPackageLabel ?? null,
    };
    // Count rows first (the live session), tolerating undeployed columns.
    if (sessionId != null) {
      const full = await client
        .from('count_items')
        .update({ ...parPatch, ...normalizationPatch })
        .eq('count_session_id', sessionId)
        .in('inventory_item_id', targetItemIds);
      if (full.error) {
        if (isMissingSchemaError(full.error)) {
          const legacy = await client
            .from('count_items')
            .update(legacyParPatch)
            .eq('count_session_id', sessionId)
            .in('inventory_item_id', targetItemIds);
          if (legacy.error) throw new Error(legacy.error.message);
        } else {
          throw new Error(full.error.message);
        }
      }
    }
    // Template rows, so the next count inherits the decision. Best-effort.
    if (templateId != null) {
      const full = await client
        .from('count_template_items')
        .update(parPatch)
        .eq('template_id', templateId)
        .in('inventory_item_id', targetItemIds);
      if (full.error && isMissingSchemaError(full.error)) {
        const legacy = await client
          .from('count_template_items')
          .update(legacyParPatch)
          .eq('template_id', templateId)
          .in('inventory_item_id', targetItemIds);
        if (legacy.error) {
          console.warn('[par-transition] template PAR propagation failed:', legacy.error.message);
        }
      } else if (full.error) {
        console.warn('[par-transition] template PAR propagation failed:', full.error.message);
      }
    }
  }

  const row = await recordParTransition({
    storeId,
    inventoryItemId,
    transition,
    recommendation,
    decision,
    resolvedBy,
    resultingNormalizedPar: resulting,
  }, client);

  return { resultingNormalizedPar: resulting, storedRow: row };
}

export { parTransitionKey };
export default detectPendingParTransitions;
